import { useState } from "react";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
} from "@tanstack/react-table";
import { formatDate } from "../utils/index";

/**
 * @typedef {object} PaginationOptions
 * @property {number} [defaultPageIndex] Index of the default page. Defaults to `0`.
 * @property {number} [defaultPageSize] Default page size for the table. This should be a value that's included in the `pageSizes` prop. Defaults to 20.
 * @property {number[]} [pageSizes] Defines page sizes that are in the dropdown. Defaults to `[5, 10, 20, 30, 40, 50, 100]`.
 * @property {boolean} [disableGoToPage] Disables the page input field.
 * @property {boolean} [disableChangePageSize] Disables the page size dropdown.
 */

/**
 * @typedef {{
 *  paginationOptions?: PaginationOptions;
 * }} CustomTableProps
 */

/**
 * @typedef {import("@tanstack/react-table").Table & CustomTableProps} CustomReactTable
 */

/**
 * Hook that wraps `useReactTable()` from `@tanstack/react-table`.
 *
 * @param {object} props
 * @param {object[]} props.data Data for the table.
 * @param {import("@tanstack/react-table").ColumnDef<unknown>[]} props.columns Columns for the table. Memoize these using `useMemo()`.
 * @param {PaginationOptions} [props.paginationOptions] Options for table pagination. Set the `undefined` or `null` to disable pagination (it's disabled by default).
 * @param {import("@tanstack/react-table").FilterFnOption<any>} props.globalFilterFn Function or string referencing a built-in filter function for filtering
 * the entire table. If passing in a function, memoize it with `useCallback()`. Defaults to `"auto"`.
 * @param {{ globalFilter: string; setGlobalFilter: React.Dispatch<React.SetStateAction<string>> }} props.globalFilterState Stateful value for the global filter and function for updating it.
 * @param {{ rowSelection: import("@tanstack/react-table").RowSelectionState; setRowSelection: React.Dispatch<React.SetStateAction<import("@tanstack/react-table").RowSelectionState>>}} props.rowSelectionState Stateful value for selected rows and function for updating it.
 * @param {import("@tanstack/react-table").SortingState} [props.defaultSort] Defines default columns to sort and direction to sort. `undefined` or `null` to disable default sorting.
 * @param {(column: import("@tanstack/react-table").Column<unknown>) => boolean} [props.getColumnCanGlobalFilter] Function that returns `true` if a column can be filtered and `false` if it cannot. The filter function will not be called if this function returns `false`. By default, the library will call the filter function if the value in the first row is a `string` or `number` type.
 * @param {import("@tanstack/react-table").TableMeta} props.meta Extra functions or data that you want available to the `table` object. The functions or data will be available via `table.options.meta`.
 * @returns {CustomReactTable}
 */
export function useTable({
    data,
    columns, // Memoize these with useMemo()
    paginationOptions,
    globalFilterFn = "auto", // Memoize this with useCallback() if passing in a function.
    globalFilterState,
    rowSelectionState,
    defaultSort,
    getColumnCanGlobalFilter,
    meta,
}) {
    // Don't see a need for managing sorting state in parent components, but pass this in as a state prop if there is at some point
    const [sorting, setSorting] = useState(defaultSort ?? []);

    // Pagination is enabled if the paginationOptions object is defined but the properties of the object are optional, so default ones that are not provided
    const defaultedPaginationOptions = paginationOptions
        ? {
              //TODO Memoize this??? Performance difference is likely negligible
              defaultPageIndex: 0,
              defaultPageSize: 20,
              pageSizes: [5, 10, 20, 30, 40, 50, 100],
              ...paginationOptions,
          }
        : null;

    // IMPORTANT: Table options are not directly passed to useReactTable because setting some options as
    // null or undefined will override the defaults built into the @tanstack/react-table library
    /** @type {import("@tanstack/react-table").TableOptions} */
    const tableOptions = {
        data: data ?? [], // The @tanstack/react-table package's API will throw an error if data is undefined, pass an empty array if data is undefined to workaround this
        columns,

        meta,

        state: {
            rowSelection: rowSelectionState?.rowSelection,
            sorting,
            globalFilter: globalFilterState?.globalFilter,
        },

        initialState: defaultedPaginationOptions
            ? {
                  pagination: {
                      pageIndex: defaultedPaginationOptions.defaultPageIndex,
                      pageSize: defaultedPaginationOptions.defaultPageSize,
                  },
              }
            : null,
        getPaginationRowModel: defaultedPaginationOptions ? getPaginationRowModel() : null, // null disables pagination

        getCoreRowModel: getCoreRowModel(),

        columnResizeMode: "onChange",

        enableRowSelection: true,
        onRowSelectionChange: rowSelectionState?.setRowSelection,

        enableSortingRemoval: false,
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),

        globalFilterFn,
        onGlobalFilterChange: globalFilterState?.setGlobalFilter, // Required for filtering all columns
        getFilteredRowModel: getFilteredRowModel(), // Required for filtering
    };

    // Attach table options that will have their defaults overridden if set to null or undefined
    if (getColumnCanGlobalFilter) {
        tableOptions.getColumnCanGlobalFilter = getColumnCanGlobalFilter;
    }

    const table = useReactTable(tableOptions);
    return {
        ...table,
        paginationOptions: defaultedPaginationOptions,
    };
}

export default useTable;

/**
 * Custom global filter function for the table.
 *
 * This function will be called once for each cell in the table.
 * It should return `true` if the row should be included and `false` otherwise.
 *
 * @callback TableFilterFunction
 * @param {import("@tanstack/react-table").Row} row The row in the table.
 * @param {string} columnId ID for the column.
 * @param {string} filterValue The value for the global filter.
 * @returns {boolean} `true` if the row should be included and `false` otherwise.
 */

/**
 * Creates a global filter function for `@tanstack/react-table`.
 *
 * @note
 * The way search and sorting works for date columns in `@tanstack/react-table` is conflicting. For sorting, the library
 * expects the date to be a `Date` object, but for searching it expects a string. This function will convert the date to
 * a string using the provided `dateFormatFn`. Documentation for sorting is here: https://tanstack.com/table/v8/docs/guide/sorting#sorting-fns.
 *
 * @param {string[]} [includedColumns]
 * Columns to include in the filter.
 *
 * If the id for a column is not explicitly set it will be derived from the accessor or header.
 * For accessor columns, the id will be the name of the accessor, e.g. `columnHelper.accessor('foo', ...)` will have the id `'foo'`.
 * If the accessor is a function, the id will be derived from the `header` property, e.g., `columnHelper.accessor(row => row.data.foo, { header: 'Foo', ... } )` will have the id `'Foo'`.
 * Documentation for this behaviour is here: https://tanstack.com/table/latest/docs/guide/columns#column-ids
 *
 * @param {(date: Date) => string} [dateFormatFn]
 * Function to format dates. Be careful changing this, you probably want this search function to use the same date format
 * that's displayed to users in the table. If the format is different, * the search results may not match what the user
 * expects (e.g., the time won't be included in the search if 'March 19, 2025 10:30 AM' * is displayed and this function
 * formats it to 'March 19, 2025').
 *
 * @returns {TableFilterFunction} The filter function.
 */
export function createTableFilterFn(includedColumns, dateFormatFn = formatDate) {
    return (row, columnId, filterValue) => {
        if (includedColumns.includes(columnId)) {
            let cellValue = row.getValue(columnId);
            if (cellValue instanceof Date && !isNaN(cellValue)) {
                cellValue = dateFormatFn(cellValue);
            }

            return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        }
    };
}
